// @flow
import React from 'react';
import { css } from 'emotion';
import { colors, fonts } from '@peachjar/components';

import ProgressBar from './ProgressBar';
import CancelIcon from './CancelIcon.svg';
import Checkmark from './Checkmark.svg';
import CsvIcon from './CsvIcon.svg';

const { prussian, dragon, slate } = colors;
const { ProximaNova } = fonts;

const ERROR_MESSAGE =
  'Uh oh! Your file could not be uploaded. Please try again.';

type CSVError = {
  lineNumber: number,
  reasons: string[],
};

type Props = {
  filename: string,
  uploadPercentage: number,
  errors: Array<CSVError>,
  status: string,
  totalCount: number,
};

export function renderIcon(
  uploadPercentage: number,
  errors: Array<CSVError>,
  status: string = ''
) {
  if (errors.length || status.toLowerCase() === 'failed') return <CancelIcon />;

  if (uploadPercentage >= 100) {
    return <Checkmark />;
  }

  return <CancelIcon />;
}

export function renderErrors(errors: Array<CSVError>): Array<any> {
  if (!errors.length)
    return [
      <div key="GenericErrorMessage">
        <span style={{ color: slate }}>{ERROR_MESSAGE}</span>
      </div>,
    ];

  
  return errors.map((error: CSVError) => (
    <ErrorMessage
      key={`parent_csv_err_${error.lineNumber}`}
      lineNumber={error.lineNumber}
      reasons={error.reasons}
    />
  ));
}

export function hasErrors(errors: Array<CSVError>) {
  return errors.length > 0;
}

export const ErrorMessage = ({ lineNumber, reasons }: CSVError) => (
  reasons.length === 1 ?
  <div>
    <span style={{ color: slate }}>{`Line ${lineNumber}: ${reasons[0]}`}</span>
  </div> :
  <div>
    <span style={{ color: slate }}>{`Line ${lineNumber}:`}</span>
    <ul>
      {reasons.map((reason) => <li>{reason}</li>)}
    </ul>
  </div>
);

const UploadStatusBox = ({
  uploadPercentage,
  filename,
  errors,
  status,
  totalCount,
}: Props) => (
  <div>
    <div className={`row ${styles.row}`}>
      <div className="col">
        <div className="row" style={{ height: '46px', width: '38px' }}>
          <CsvIcon />
        </div>
      </div>
      <div className="col-11 d-flex flex-column justify-content-center">
        <div
          className={`row d-flex justify-content-between ${styles.filename}`}
        >
          {filename}
          {renderIcon(uploadPercentage, errors, status)}
        </div>
        <div className="row" style={{ marginTop: '8px' }}>
          <ProgressBar
            percentage={uploadPercentage}
            message={
              hasErrors(errors) || status === 'failed' ? 'error' : 'success'
            }
          />
          {hasErrors(errors) || status === 'failed' ? (
            <div className="d-flex flex-column">
              <span style={{ color: dragon }}>
                {status === 'failed' ? 'Upload Error' : 'Invalid Information'}:
              </span>
              {renderErrors(errors)}
            </div>
          ) : (
            <div className={styles.totalCount}>{`Total Count: ${totalCount ||
              0}`}</div>
          )}
        </div>
      </div>
    </div>
  </div>
);

UploadStatusBox.defaultProps = {
  errors: [],
};

const styles = {
  filename: css`
    font-family: ${ProximaNova};
    font-size: 19px;
    font-weight: bold;
    font-style: normal;
    font-stretch: normal;
    line-height: 1.26;
    letter-spacing: normal;
    color: ${prussian};
  `,
  row: css`
    width: 920px;
    min-height: 56px;
    padding: 16px 19px;
    margin-bottom: 16px;
    border: 1px solid #e6e6e6;
    border-radius: 3px;
    margin-left: 0 !important;
  `,
  totalCount: css`
    margin-top: 8px;
    font-size: 13px;
  `,
};

export default UploadStatusBox;



// WEBPACK FOOTER //
// ./src/Onboarding/Setup/parent/components/UploadStatusBox.jsx